import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { Field } from './Modal';
import { Chip } from './StatusChip';
import ErrorState from './ErrorState';
import EmptyState from './EmptyState';
import { useConfirm } from './ConfirmDialog';
import { apiFetch, apiErrorMessage } from '../hooks/useApi';
import { formatDate, plural, todayLocalISO } from '../utils/format';

/**
 * <ClientPortalSettings /> — create & manage secure, no-login capability links that
 * give a client a read-only view of one property or a portfolio (compliance review
 * dates + client-visible documents only). Links can carry an access code, expire,
 * and be revoked. Revoked links stay listed (with a "Revoked" chip) as a record of
 * who had access. Rendered on the Client portal page.
 */
const AUDIENCES = [
  { key: 'client', label: 'Client', hint: 'The company that instructed the work' },
  { key: 'owner', label: 'Property owner', hint: 'Landlord / freeholder' },
  { key: 'maintainer', label: 'Maintenance contractor', hint: 'Whoever services the equipment on site' },
];
const DEFAULT_EXPIRY_DAYS = 90;   // the server's default when no date is given
const MIN_CODE = 6;
const NEW_INIT = { label: '', audience: 'client', propertyIds: [], show_compliance: true, show_documents: true, allow_client_branding: true, expires_at: '', access_code: '' };

const linkState = (l) => (l.revoked ? 'revoked' : (l.expires_at && new Date(l.expires_at) < new Date()) ? 'expired' : 'active');
const STATE_CHIP = { active: ['success', 'Active'], expired: ['warning', 'Expired'], revoked: ['danger', 'Revoked'] };

const Stat = ({ value, label }) => (
  <div className="cw-card cw-card-pad" style={{ flex: '1 1 min(130px, 100%)' }}>
    <div style={{ fontSize: 'var(--cs-text-2xl)', fontWeight: 700, color: 'var(--cw-text)' }}>{value}</div>
    <div className="cw-label">{label}</div>
  </div>
);

const Toggle = ({ checked, onChange, children, hint }) => (
  <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 'var(--cs-text-sm)', cursor: 'pointer', minHeight: 44 }}>
    <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} style={{ width: 18, height: 18, marginTop: 2 }} />
    <span>{children}{hint && <span className="cw-meta" style={{ display: 'block' }}>{hint}</span>}</span>
  </label>
);

const copyText = async (text) => {
  if (!navigator.clipboard?.writeText) return false;
  try { await navigator.clipboard.writeText(text); return true; } catch { return false; }
};

const ClientPortalSettings = () => {
  const { confirm } = useConfirm();
  const [links, setLinks] = useState([]);
  const [linksLoading, setLinksLoading] = useState(true);
  const [linksError, setLinksError] = useState(null);
  const [properties, setProperties] = useState([]);
  const [propsError, setPropsError] = useState(null);
  const [form, setForm] = useState(NEW_INIT);
  const [errors, setErrors] = useState({});
  const [creating, setCreating] = useState(false);
  const [propQuery, setPropQuery] = useState('');
  const [showRevoked, setShowRevoked] = useState(true);
  const [justCreated, setJustCreated] = useState(null);   // { url, copied }

  const load = useCallback(async () => {
    setLinksLoading(true);
    try { const d = await apiFetch('/api/contractor/portal-links'); setLinks(Array.isArray(d?.links) ? d.links : []); setLinksError(null); }
    catch (e) { setLinksError(e); }
    finally { setLinksLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const loadProperties = useCallback(async () => {
    const out = []; let err = null;
    try {
      const d = await apiFetch('/api/contractor/properties');
      const list = Array.isArray(d) ? d : (d?.properties || []);
      list.filter((p) => p.id != null).forEach((p) => out.push({ id: p.id, name: p.name || p.address || `Property ${p.id}`, local: false }));
    } catch (e) { err = e; }
    try {
      const d = await apiFetch('/api/contractor/local-properties');
      (Array.isArray(d?.properties) ? d.properties : []).forEach((p) => out.push({ id: `local-${p.id}`, name: p.name || `Property ${p.id}`, local: true }));
    } catch (e) { err = err || e; }
    setProperties(out);
    setPropsError(err);
  }, []);
  useEffect(() => { loadProperties(); }, [loadProperties]);

  const stats = useMemo(() => ({
    total: links.length,
    active: links.filter((l) => linkState(l) === 'active').length,
    coded: links.filter((l) => l.has_code && linkState(l) === 'active').length,
    views: links.reduce((a, l) => a + (l.view_count || 0), 0),
  }), [links]);

  const filteredProps = useMemo(() => {
    const q = propQuery.trim().toLowerCase();
    return q ? properties.filter((p) => p.name.toLowerCase().includes(q)) : properties;
  }, [properties, propQuery]);

  const setF = (patch) => { setForm((f) => ({ ...f, ...patch })); setErrors((e) => { const n = { ...e }; Object.keys(patch).forEach((k) => { delete n[k]; }); if (patch.propertyIds) delete n.propertyIds; return n; }); };
  const toggleProp = (id) => setF({ propertyIds: form.propertyIds.includes(id) ? form.propertyIds.filter((x) => x !== id) : [...form.propertyIds, id] });

  const create = async () => {
    const errs = {};
    if (!form.propertyIds.length) errs.propertyIds = 'Choose at least one property';
    const code = form.access_code.trim();
    if (code && code.length < MIN_CODE) errs.access_code = `Use at least ${MIN_CODE} characters — short codes are easy to guess`;
    if (form.expires_at && form.expires_at <= todayLocalISO()) errs.expires_at = 'Pick a date after today';
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setCreating(true);
    try {
      const scope_type = form.propertyIds.length > 1 ? 'portfolio' : 'property';
      const res = await apiFetch('/api/contractor/portal-links', { method: 'POST', body: JSON.stringify({ ...form, access_code: code, expires_at: form.expires_at || undefined, scope_type, property_ids: form.propertyIds }) });
      const copied = res?.url ? await copyText(res.url) : false;
      toast.success(copied ? 'Link created and copied' : 'Client portal link created');
      setJustCreated(res?.url ? { url: res.url, copied } : null);
      setForm(NEW_INIT); setPropQuery('');
      load();
    } catch (e) { toast.error(`Couldn't create the link: ${apiErrorMessage(e)}`); }
    finally { setCreating(false); }
  };

  const copyLink = async (url) => {
    if (await copyText(url)) toast.success('Link copied');
    else toast.info('Your browser blocked copying — select the link text and copy it by hand.');
  };
  const revoke = async (l) => {
    const ok = await confirm({
      title: 'Revoke this link?',
      body: `Anyone holding “${l.label || 'this link'}” loses access immediately. It stays in your list as revoked, as a record of who had access.`,
      confirmLabel: 'Revoke link', tone: 'danger',
    });
    if (!ok) return;
    const before = links;
    setLinks((prev) => prev.map((x) => (x.id === l.id ? { ...x, revoked: 1 } : x)));
    try { await apiFetch(`/api/contractor/portal-links/${l.id}`, { method: 'PATCH', body: JSON.stringify({ revoked: true }) }); toast.success('Link revoked'); }
    catch (e) { setLinks(before); toast.error(`Couldn't revoke the link: ${apiErrorMessage(e)}`); }
  };
  const removeRecord = async (l) => {
    const ok = await confirm({ title: 'Delete this revoked link from the list?', body: 'Its view history is deleted too. This can’t be undone.', confirmLabel: 'Delete record', tone: 'danger' });
    if (!ok) return;
    try { await apiFetch(`/api/contractor/portal-links/${l.id}`, { method: 'DELETE' }); setLinks((prev) => prev.filter((x) => x.id !== l.id)); toast.success('Link record deleted'); }
    catch (e) { toast.error(`Couldn't delete the record: ${apiErrorMessage(e)}`); }
  };

  const shown = showRevoked ? links : links.filter((l) => linkState(l) !== 'revoked');
  const revokedCount = links.filter((l) => linkState(l) === 'revoked').length;
  const defaultExpiry = formatDate(new Date(Date.now() + DEFAULT_EXPIRY_DAYS * 864e5));

  return (
    <div>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 18 }}>
        <Stat value={stats.total} label="Links created" />
        <Stat value={stats.active} label="Active" />
        <Stat value={stats.coded} label="Active with a code" />
        <Stat value={stats.views} label="Total views" />
      </div>

      <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        {/* Create panel */}
        <section className="cw-card cw-card-pad" style={{ flex: '1 1 340px', minWidth: 0 }} aria-labelledby="cw-portal-new">
          <h3 id="cw-portal-new" className="cw-h3" style={{ marginBottom: 4 }}><i className="fa-solid fa-link" style={{ color: 'var(--cw-blue)', marginRight: 8 }} aria-hidden="true" />New share link</h3>
          <p className="cw-meta" style={{ margin: '0 0 14px' }}>A read-only view of the properties you pick — compliance dates and client-visible documents only.</p>

          <Field label="Label (optional)">
            <input value={form.label} placeholder="e.g. Quarterly compliance — Acme FM" onChange={(e) => setF({ label: e.target.value })} />
          </Field>

          <Field label="Who is this link for?" hint={`${AUDIENCES.find((a) => a.key === form.audience)?.hint} — they only see the fields you've shared with this audience.`}>
            <select value={form.audience} onChange={(e) => setF({ audience: e.target.value })}>
              {AUDIENCES.map((a) => <option key={a.key} value={a.key}>{a.label}</option>)}
            </select>
          </Field>

          <fieldset style={{ border: 'none', padding: 0, margin: '12px 0 0' }} aria-describedby={errors.propertyIds ? 'cw-portal-props-err' : undefined}>
            <legend className="cw-label" style={{ display: 'flex', justifyContent: 'space-between', width: '100%', marginBottom: 4 }}>
              <span>Properties <span className="cw-req" aria-hidden="true">*</span></span>
            </legend>
            {form.propertyIds.length > 0 && <p className="cw-meta" style={{ margin: '0 0 6px' }} aria-live="polite">{plural(form.propertyIds.length, 'property', 'properties')} selected{form.propertyIds.length > 1 ? ' · portfolio link' : ''}</p>}
            {propsError && <ErrorState compact thing="your properties" error={propsError} onRetry={loadProperties} />}
            {properties.length === 0 && !propsError ? (
              <p className="cw-meta" style={{ margin: 0 }}>No properties yet — add one on the Properties page first.</p>
            ) : properties.length > 0 && (
              <>
                {properties.length > 6 && (
                  <input type="search" className="cw-input" style={{ marginBottom: 6 }} value={propQuery} aria-label="Search properties" placeholder="Search properties…" onChange={(e) => setPropQuery(e.target.value)} />
                )}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2, maxHeight: 200, overflowY: 'auto', border: `1px solid ${errors.propertyIds ? 'var(--cw-danger)' : 'var(--cw-border-light)'}`, borderRadius: 8, padding: 6 }}>
                  {filteredProps.map((p) => {
                    const on = form.propertyIds.includes(p.id);
                    return (
                      <label key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 'var(--cs-text-sm)', cursor: 'pointer', padding: '6px', minHeight: 40, borderRadius: 6, background: on ? 'var(--cw-blue-tint)' : 'transparent' }}>
                        <input type="checkbox" checked={on} onChange={() => toggleProp(p.id)} style={{ width: 18, height: 18 }} />
                        <span style={{ flex: 1, minWidth: 0 }}>{p.name}</span>
                        {p.local && <Chip tone="warning" label="Not on CloudSafe" />}
                      </label>
                    );
                  })}
                  {filteredProps.length === 0 && <div className="cw-meta" style={{ padding: 6 }}>No matches.</div>}
                </div>
              </>
            )}
            {errors.propertyIds && <p id="cw-portal-props-err" className="cw-field-error" role="alert"><i className="fa-solid fa-circle-exclamation" aria-hidden="true" /> {errors.propertyIds}</p>}
          </fieldset>

          <fieldset style={{ border: 'none', padding: 0, margin: '12px 0' }}>
            <legend className="cw-label">What the link shows</legend>
            <Toggle checked={form.show_compliance} onChange={(v) => setF({ show_compliance: v })}>Compliance dates</Toggle>
            <Toggle checked={form.show_documents} onChange={(v) => setF({ show_documents: v })}>Client-visible documents</Toggle>
            <Toggle checked={form.allow_client_branding} onChange={(v) => setF({ allow_client_branding: v })} hint="Accent colour, logo, header/footer and cover notes — presentation only.">Let the client personalise branding</Toggle>
          </fieldset>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(160px, 100%), 1fr))', gap: 12 }}>
            <Field label="Expires" error={errors.expires_at} hint={form.expires_at ? undefined : `Leave blank for ${DEFAULT_EXPIRY_DAYS} days (${defaultExpiry})`}>
              <input type="date" min={todayLocalISO()} value={form.expires_at} onChange={(e) => setF({ expires_at: e.target.value })} />
            </Field>
            <Field label="Access code (optional)" error={errors.access_code} hint={`At least ${MIN_CODE} characters`}>
              <input value={form.access_code} autoComplete="off" spellCheck={false} placeholder="word or PIN" onChange={(e) => setF({ access_code: e.target.value })} />
            </Field>
          </div>

          <div role="note" style={{ display: 'flex', alignItems: 'flex-start', gap: 8, background: form.access_code.trim() ? 'var(--cw-success-tint)' : 'var(--cw-warning-tint)', border: `1px solid ${form.access_code.trim() ? 'var(--cs-green-border, #bbf7d0)' : 'var(--cw-warning-border)'}`, borderRadius: 8, padding: '8px 10px', margin: '4px 0 14px' }}>
            <i className={`fa-solid ${form.access_code.trim() ? 'fa-shield-halved' : 'fa-triangle-exclamation'}`} style={{ color: form.access_code.trim() ? 'var(--cw-success-fg)' : 'var(--cw-warning-fg)', marginTop: 2 }} aria-hidden="true" />
            <span style={{ fontSize: 'var(--cs-text-sm)', color: 'var(--cw-text-2)' }}>
              {form.access_code.trim()
                ? 'Protected — the client must enter this code to open the link. Send the code separately (text or call), not with the link.'
                : 'Link only — anyone with the link can view it. For sensitive information, add an access code.'}
            </span>
          </div>

          <button type="button" className="cw-btn cw-btn-primary" onClick={create} disabled={creating} aria-busy={creating || undefined} style={{ width: '100%', justifyContent: 'center' }}>
            <i className="fa-solid fa-link" aria-hidden="true" /> {creating ? 'Creating…' : 'Create secure link'}
          </button>
          {justCreated && (
            <div role="status" style={{ marginTop: 12, fontSize: 'var(--cs-text-sm)' }}>
              <div style={{ fontWeight: 600 }}>{justCreated.copied ? 'Copied to your clipboard:' : 'Your new link (copy it from here):'}</div>
              <input readOnly className="cw-input" value={justCreated.url} aria-label="New share link" onFocus={(e) => e.target.select()} style={{ marginTop: 4, fontFamily: 'ui-monospace, monospace' }} />
            </div>
          )}
        </section>

        {/* Links list */}
        <section style={{ flex: '2 1 420px', minWidth: 0 }} aria-labelledby="cw-portal-links">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
            <h3 id="cw-portal-links" style={{ fontSize: 'var(--cs-text-base)', fontWeight: 700, margin: 0 }}>Share links {links.length > 0 && <span className="cw-meta" style={{ fontWeight: 500 }}>({links.length})</span>}</h3>
            {revokedCount > 0 && (
              <label style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6, fontSize: 'var(--cs-text-sm)', cursor: 'pointer' }}>
                <input type="checkbox" checked={showRevoked} onChange={(e) => setShowRevoked(e.target.checked)} /> Show revoked ({revokedCount})
              </label>
            )}
          </div>
          {linksError ? <ErrorState thing="share links" error={linksError} onRetry={load} retrying={linksLoading} />
            : linksLoading && !links.length ? <div role="status" className="cw-meta">Loading links…</div>
            : shown.length === 0 ? (
              <EmptyState icon="fa-solid fa-share-nodes" title={links.length ? 'No active links' : 'No share links yet'} body="Create one to give a client a read-only view." />
            ) : (
              <ul style={{ display: 'flex', flexDirection: 'column', gap: 10, listStyle: 'none', margin: 0, padding: 0 }}>
                {shown.map((l) => {
                  const st = linkState(l);
                  const [tone, label] = STATE_CHIP[st];
                  const live = st === 'active';
                  const name = l.label || (l.scope_type === 'portfolio' ? 'Portfolio link' : 'Property link');
                  return (
                    <li key={l.id} className="cw-card" style={{ padding: 14, background: live ? undefined : 'var(--cw-surface-alt)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
                        <span style={{ fontSize: 'var(--cs-text-base)', fontWeight: 700 }}>{name}</span>
                        <Chip tone={tone} label={label} dot />
                        {l.has_code
                          ? <Chip tone="success" label={<><i className="fa-solid fa-lock" aria-hidden="true" /> Access code</>} />
                          : <Chip tone="warning" label={<><i className="fa-solid fa-lock-open" aria-hidden="true" /> Link only</>} />}
                        {l.audience && <Chip tone="neutral" label={AUDIENCES.find((a) => a.key === l.audience)?.label || l.audience} />}
                      </div>
                      <div className="cw-meta" style={{ marginBottom: 8 }}>
                        {plural((l.property_ids || []).length, 'property', 'properties')} · {plural(l.view_count || 0, 'view')}
                        {l.last_viewed_at && ` · last viewed ${formatDate(l.last_viewed_at)}`}
                        {l.created_at && ` · created ${formatDate(l.created_at)}`}
                        {l.expires_at && <> · <span style={{ color: st === 'expired' ? 'var(--cw-danger-fg)' : undefined }}>{st === 'expired' ? 'expired' : 'expires'} {formatDate(l.expires_at)}</span></>}
                      </div>
                      {live ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <input readOnly className="cw-input" value={l.url} aria-label={`Link for ${name}`} onFocus={(e) => e.target.select()}
                            style={{ flex: '1 1 200px', minWidth: 0, fontFamily: 'ui-monospace, monospace', fontSize: 'var(--cs-text-sm)' }} />
                          <button type="button" className="cw-btn cw-btn-secondary" onClick={() => copyLink(l.url)} aria-label={`Copy link for ${name}`}><i className="fa-solid fa-copy" aria-hidden="true" /> Copy</button>
                          <a className="cw-btn cw-btn-secondary" href={l.url} target="_blank" rel="noopener noreferrer" title="Opens exactly what the client sees. Counts as a view; code-protected links ask for the code.">
                            <i className="fa-solid fa-eye" aria-hidden="true" /> Preview as client<span className="cw-sr-only"> (opens in a new tab and counts as a view)</span>
                          </a>
                          <button type="button" className="cw-btn cw-btn-danger-outline" onClick={() => revoke(l)} aria-label={`Revoke ${name}`}><i className="fa-solid fa-ban" aria-hidden="true" /> Revoke</button>
                        </div>
                      ) : st === 'revoked' ? (
                        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                          <button type="button" className="cw-btn-link danger" onClick={() => removeRecord(l)} aria-label={`Delete the record of ${name}`}>Delete record</button>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                          <button type="button" className="cw-btn cw-btn-danger-outline" onClick={() => revoke(l)} aria-label={`Revoke ${name}`}><i className="fa-solid fa-ban" aria-hidden="true" /> Revoke</button>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
        </section>
      </div>
    </div>
  );
};

export default ClientPortalSettings;
